import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Authorize from '../../../components/AuthComponent/Authorize';
import SaveButton from '../../../components/Buttons/SaveButton';
import ButtonBar from '../../../components/Fields/ButtonBar';
import DateField from '../../../components/Fields/DateField';
import Form from '../../../components/Fields/Form';
import SelectListField from '../../../components/Fields/SelectListField';
import TextareaField from '../../../components/Fields/TextareaField';
import TextboxField from '../../../components/Fields/TextboxField';
import { FieldConfig, FormConfig, ValidatorConfig } from '../../../components/Fields/Types';
import UserDefinedField from '../../../components/Fields/UserDefinedField';
import Title from '../../../components/Headers/Title';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import { useNotifications } from '../../../contexts/notificationContext';
import { useTranslations } from '../../../contexts/translationContext';
import ProjectMilestoneModel from '../../../models/ProjectMilestoneModel';
import ProjectTypeModel from '../../../models/ProjectTypeModel';
import ProjectUserUpdateModel from '../../../models/ProjectUserUpdateModel';
import UserDefinedFieldValueModel from '../../../models/UserDefinedFieldValueModel';
import DataService from '../../../services/data.service';
import { Result } from '../../../types/Result';
import FormatUtils from '../../../utils/FormatUtils';
import Unauthorized from '../../Authentication/Unauthorized';
import ProjectMilestoneField from './../Components/ProjectMilestoneField';
import ProjectUserField from './ProjectUserField';

interface ProjectCreateModel {
    projectTypeId?: number
    projectStatusId: number
    name: string
    description: string
    startedOn: string
    deadline?: string
    finishedOn?: string
    projectCode: string

    userDefinedFields: UserDefinedFieldValueModel[]
    milestones: ProjectMilestoneModel[]
    users: ProjectUserUpdateModel[]
}

interface IProps {
    onProjectCreated: (projectId: number) => void
}

const ProjectDetailsView: React.FC<IProps> = ({ onProjectCreated }) => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const translate = useTranslations();
    const navigate = useNavigate();
    const [projectType, setProjectType] = useState<ProjectTypeModel>();
    const [udfVisible, setUdfVisible] = useState(true)
    const [showSpinner, setShowSpinner] = useState(false);

    const [formData, setFormData] = useState<ProjectCreateModel>({
        name: '',
        projectStatusId: 1,
        description: '',
        startedOn: new FormatUtils().formatDateForStorage(new Date()),
        projectCode: '',
        userDefinedFields: [],
        milestones: [],
        users: [],
    });

    const projectTypes = useSelectListOptions('api/v1/selectlist/ProjectTypes/?ApplicationFunction=' + ApplicationFunctions.CreateProject, '-- Select project type');

    useEffect(() => {
        if (formData?.projectTypeId !== undefined) {
            setShowSpinner(true);
            DataService.getById<ProjectTypeModel>('api/v1/ProjectTypes/', formData.projectTypeId)
                .then(response => {
                    setShowSpinner(false);
                    if (response.fields)
                        setUdfVisible(response.fields.filter(x => x.userDefinedFieldTypeId == 6).length > 0)

                    setProjectType(response);
                })
                .catch(e => {
                    setShowSpinner(false);
                    handleException(e);
                });

            DataService.get<UserDefinedFieldValueModel[]>('api/v1/projectuserdefinedfields/fornewproject/' + formData.projectTypeId)
                .then(response => {
                    formData.userDefinedFields = [...response];
                    setFormData({ ...formData });
                })
                .catch(e => handleException(e));
        }
    }, [formData.projectTypeId])

    useEffect(() => {
        if (formData.projectTypeId) {
            DataService.post<ProjectUserUpdateModel[]>('api/v1/projectUsers/forNewProject/', formData)
                .then(response => {
                    formData.users = response;
                    setFormData({ ...formData });
                })
                .catch(e => handleException(e));
        }
    }, [formData.projectTypeId]);

    useEffect(() => {
        if (formData?.projectTypeId !== undefined) {
            DataService.get<ProjectMilestoneModel[]>('api/v1/milestones/fornewproject/' + formData.projectTypeId + '/' + formData.deadline)
                .then(response => {
                    console.log(response);
                    formData.milestones = [...response];
                    setFormData({ ...formData });


                })
                .catch(e => handleException(e));
        }
    }, [formData.projectTypeId, formData.deadline])

    function submit() {
        setShowSpinner(true);
        if (formData) {
            DataService
                .create<number>(`api/v1/Projects/`, formData)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        onProjectCreated(response.data);
                })
                .catch(e => { setShowSpinner(false); handleException(e); });
        }
    }

    let fields: FieldConfig[] = [
        { name: 'projectTypeId', label: 'Project type', labelSize: 4 },
        { name: 'name', label: 'Name', labelSize: 4, validationRules: [{ type: 'isRequired' }] },
        { name: 'code', label: 'Projectcode', labelSize: 4 },
        { name: 'description', label: 'Description', labelSize: 4 },
        { name: 'startedOn', label: 'Started on', labelSize: 5, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
        { name: 'deadline', label: 'Deadline', labelSize: 5 },
        { name: 'finishedOn', label: 'Finished on', labelSize: 5 },
    ];

    if (formData && formData.userDefinedFields) {
        formData.userDefinedFields.forEach((value, index) => {
            let label = value.userDefinedField.name
            fields.push({ name: 'userDefinedFields.' + index + '.value', label: label, labelSize: 4 });
        });
    }

    if (formData && formData.milestones) {
        formData.milestones.forEach((value, index) => {
            fields.push({ name: 'milestones.' + index + '.milestoneDate', label: value.milestoneName, labelSize: 5 });
        });
    }

    if (formData && formData.users) {
        formData.users.forEach((value, index) => {
            let label = value.functionalRole.name;
            let validationRules: ValidatorConfig[] = []
            if (value.isRequired)
                validationRules.push({ type: 'isRequired' });

            fields.push({ name: 'users.' + index + '.userIds', label: label, labelSize: 3, validationRules });
        });
    }

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);
    const { isAuthorizedForProjectType } = useAuth();

    if (!isAuthorizedForProjectType(ApplicationFunctions.CreateProject))
        return (<Unauthorized />);

    return (
        <div className='main-content'>
            <Form title={'Create new project'} footer={(
                <Row>
                    <Col md='5'>
                        <ButtonBar offset={3}>
                            <Authorize applicationFunction={ApplicationFunctions.CreateProject} projectTypeId={formData ? formData.projectTypeId : 0}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                    <Col md='7' className='hidden-md'>
                        <ButtonBar offset={0}>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='6'>
                            <SelectListField {...getFieldProps('projectTypeId')} options={projectTypes} />
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('code')} />
                            {formData && formData.userDefinedFields &&
                                formData.userDefinedFields.map((value, index) => {
                                    let name = 'userDefinedFields.' + index + '.value';
                                    return (<UserDefinedField {...getFieldProps(name)} userDefinedFieldValue={value} key={'userDefinedField' + index} />);
                                })
                            }
                            <TextareaField {...getFieldProps('description')} rows={5} cols={50} />
                            {formData.users && formData.users.length > 0 && <Title text='Employees' size={5} />}
                            {formData.users && (
                                formData.users.map((projectUser: ProjectUserUpdateModel, index: number) => {
                                    return <ProjectUserField {...getFieldProps(`users.${index}.userIds`)} projectUser={projectUser} key={'user-' + index} />
                                })
                            )}
                        </Col>
                        <Col md='6'>
                            <DateField {...getFieldProps('startedOn')} />
                            <DateField {...getFieldProps('deadline')} />
                            {formData && formData.milestones && formData.milestones.length > 0 && <Title text='Milestones' size={4} />}
                            {formData && formData.milestones &&
                                formData.milestones.map((value, index) => {
                                    let name = 'milestones.' + index + '.milestoneDate';
                                    return (<ProjectMilestoneField {...getFieldProps(name)} milestone={value} key={'milestones.' + index} />);
                                })
                            }
                        </Col>

                    </Row>
                </Container>
            </Form>
            {showSpinner && <SpinnerOverlay />}
        </div>
    );
}
export default ProjectDetailsView;